import {
  selectIsConnectedToRoom,
  selectMessagesByPeerID,
  selectPeers,
  useAVToggle,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { useQuery } from "@apollo/client";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { GET_SERVICE_PROVIDER_APPOINTMENTS } from "../../apollo/appointment/appointment.queries";
import { currentPeerInterface } from "../../interfaces";
import { dateService, loggerService, networkService } from "../../services";
import { useSidebarStore } from "../../store";
import { Header } from "../shared/components";
import { useServiceProvider } from "../shared/hooks";
import Peer from "./meeting-room-peer";

import { API_DATE_FORMAT } from "../../services/date.service";

import MeetingRoomAppointments from "./meeting-room-appointments";

const MeetingRoom: React.FC = () => {
  const toggleSidebar = useSidebarStore((state) => state.toggleSidebar);

  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio, toggleVideo } =
    useAVToggle();

  const [chatMessage, setChatMessage] = useState<string>("");
  const [chats, setChats] = useState<any[]>([]);

  const [peerChatId, setPeerChatId] = useState("");

  const [waitingList, setWaitingList] = useState<any[]>([]);
  const [scheduledList, setScheduledList] = useState<any[]>([]);
  const [availableRoomId, setAvailableRoomId] = useState<string>("");

  const [isCheckedIn, setIsCheckedIn] = useState<boolean>(false);
  const [isCheckingIn, setIsCheckingIn] = useState<boolean>(false);

  const [currentPatient, setCurrentPatient] =
    useState<currentPeerInterface>(null);

  const [time, setTime] = useState<number>(0);
  const [start, setStart] = useState<boolean>(false);

  const serviceProvider = useServiceProvider();

  const hmsActions = useHMSActions();
  const peers = useHMSStore(selectPeers);
  const isConnected = useHMSStore(selectIsConnectedToRoom);

  const peerChatMessages = useHMSStore(
    // selectMessagesByPeerID(selectedPeerForChat.id)
    selectMessagesByPeerID(peerChatId)
  );

  const [displayChatScreen, setDisplayChatScreen] = useState<boolean>(false);

  useEffect(() => {
    const isToggledIn = JSON.parse(sessionStorage.getItem("toggleMeet"));
    if (!isToggledIn) return;
    if (isToggledIn.state.toggleMeet) {
      handleCheckin();
    }
  }, []);

  useEffect(() => {
    let interval = null;

    if (start) {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime + 10);
      }, 10);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [start]);

  useEffect(() => {
    window.onunload = async () => {
      if (isConnected) {
        try {
          await hmsActions.leave();
          setIsCheckedIn(false);
        } catch (error) {
          console.log("Error leaving room", error);
        }
      }
    };
  }, [hmsActions, isConnected]);

  useEffect(() => {
    if (!peerChatMessages) {
      return;
    }
    setChats(peerChatMessages);
  }, [peerChatMessages]);

  useEffect(() => {
    for (let i = 0; i < peers.length; i++) {
      if (peers[i].roleName === "patient") {
        setCurrentPatient(peers[i]);
        setStart(true);
        break;
      }
    }
  }, [peers]);

  useEffect(() => {
    if (!isCheckedIn) return;
    const helper = async () => {
      try {
        const waitingData = await networkService
          .get<any>(
            `${process.env.REACT_APP_OPEN_TELE_URL}api/consumers/waiting-consumers?serviceProviderId=${serviceProvider.id}`
          )
          .catch((e) => {
            throw new Error(e);
          });
        setWaitingList([...waitingData]);
      } catch (error) {
        throw new Error(error);
      }
    };

    setTimeout(() => {
      helper();
    }, 5000);
  }, [peers.length]);

  const FROM_DATE = dateService.getMomentDate().format(API_DATE_FORMAT),
    TO_DATE = dateService.getMomentDate().format(API_DATE_FORMAT);

  const { data } = useQuery<{ appointments: [] }>(
    GET_SERVICE_PROVIDER_APPOINTMENTS,
    {
      variables: {
        appointmentFilter: {
          fromDate: FROM_DATE,
          toDate: TO_DATE,
          serviceProviderId: serviceProvider.id,
        },
      },
    }
  );

  useEffect(() => {
    if (data?.appointments) setScheduledList(data.appointments);
  }, [data]);

  const handleNextPatient = async () => {
    try {
      if (waitingList.length === 0) return;
      let peerid = "";
      peers.forEach((element) => {
        if (
          element.roleName !== "serviceprovider" &&
          element.roleName !== "waiting"
        )
          peerid = element.id;
      });
      await networkService.post<any>(
        `${process.env.REACT_APP_OPEN_TELE_URL}api/rooms/next-patient`,
        {
          roomId: availableRoomId,
          currentTime: new Date(),
          currentPatientMsId: peerid,
        }
      );
      setTime(0);
    } catch (error) {
      console.log("Error while fetching waiting list", error);
    }
  };

  const handleOpenChat = () => {
    if (!currentPatient) return;
    if (displayChatScreen) {
      setPeerChatId(currentPatient.id);
    } else {
      setPeerChatId(currentPatient.id);
      setDisplayChatScreen(!displayChatScreen);
    }
  };

  const onChatSend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    hmsActions
      .sendDirectMessage(chatMessage, peerChatId)
      .catch((e) => console.log(e));
    setChatMessage("");
  };

  const handleChatWithWaiting = (id) => {
    if (displayChatScreen) {
      setPeerChatId(id);
    } else {
      setPeerChatId(id);
      setDisplayChatScreen(!displayChatScreen);
    }
  };

  const handleCloseChat = () => {
    setDisplayChatScreen(!displayChatScreen);
    setPeerChatId("");
  };

  const handleLinkCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        `https://open-tele.app.100ms.live/preview/vyo-ojc-tfy`
      );
      toast.success("Link copied to clipboard");
    } catch (error) {
      console.log("Error while copying link to clipboard", error);
    }
  };

  const handleCheckout = async () => {
    try {
      await hmsActions.leave();
      setIsCheckedIn(false);
      setStart(false);
      setTime(0);
      setIsCheckingIn(false);
      sessionStorage.setItem(
        "toggleMeet",
        JSON.stringify({ state: { toggleMeet: false } })
      );
    } catch (error) {
      console.log("Error leaving room", error);
    }
  };

  const handleCheckin = async () => {
    setIsCheckingIn(true);
    try {
      const userName = "Provider";
      const res = await networkService.get<any>(
        `${process.env.REACT_APP_OPEN_TELE_URL}api/rooms?serviceProviderId=${serviceProvider.id}`
      );
      setAvailableRoomId(res.roomId);
      await hmsActions.join({
        userName,
        authToken: res.appToken,
        settings: {
          isAudioMuted: true,
          isVideoMuted: false,
        },
      });
      sessionStorage.setItem(
        "toggleMeet",
        JSON.stringify({ state: { toggleMeet: true } })
      );
      setIsCheckedIn(true);
      setIsCheckingIn(false);
    } catch (error) {
      loggerService.error(error);
      setIsCheckingIn(false);
    }
  };

  return (
    <div className="flex">
      <ToastContainer />
      {/* Today's Appointments panel */}
      <MeetingRoomAppointments
        scheduledList={scheduledList}
        waitingList={waitingList}
        handleCheckin={handleCheckin}
        handleCheckout={handleCheckout}
        isCheckedIn={isCheckedIn}
        currentPatient={currentPatient}
        handleChatWithWaiting={handleChatWithWaiting}
        isCheckingIn={isCheckingIn}
      />
      {/* Right Panel */}
      <div className="w-full">
        <Header
          title={`Patient - ${currentPatient?.name || ""}`}
          setSidebarOpen={toggleSidebar}>
          <div
            className=" flex items-center gap-x-8"
            onClick={() => setStart(true)}>
            <span className="text-gray-500 border px-4 py-2 rounded-md">
              Time Spent
              <span className="text-green-400 text-lg font-semibold ml-2">
                {`0${Math.floor((time / 60000) % 60)}`.slice(-2)}
              </span>
              <span className="text-green-400 ml-1">min</span>
              <span className="text-green-400 text-lg font-semibold ml-1">
                :
              </span>
              <span className="text-green-400 text-lg font-semibold ml-1">
                {`0${Math.floor((time / 1000) % 60)}`.slice(-2)}
              </span>
              {/* <span className="text-green-400 text-lg font-semibold ml-2">
                {`0${((time / 10) % 1000).toFixed(0).slice(-2)}`}
              </span> */}
              <span className="text-green-400 ml-1">sec</span>
            </span>

            <button className="bg-primary p-3 rounded-md grid place-items-center">
              <svg
                width="125"
                height="15"
                viewBox="0 0 125 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0.289648 7.31V5.975H3.12465V3.32H4.50465V5.975H7.33965V7.31H4.50465V9.98H3.12465V7.31H0.289648ZM13.0816 11V0.499999H14.6716V11H13.0816ZM16.7423 11V3.56H18.1523L18.2723 4.865C18.5023 4.405 18.8373 4.045 19.2773 3.785C19.7273 3.515 20.2423 3.38 20.8223 3.38C21.7223 3.38 22.4273 3.66 22.9373 4.22C23.4573 4.78 23.7173 5.615 23.7173 6.725V11H22.1423V6.89C22.1423 5.45 21.5523 4.73 20.3723 4.73C19.7823 4.73 19.2923 4.94 18.9023 5.36C18.5223 5.78 18.3323 6.38 18.3323 7.16V11H16.7423ZM27.5578 11L24.7678 3.56H26.4328L28.5028 9.485L30.5728 3.56H32.2228L29.4478 11H27.5578ZM34.4294 2.15C34.1294 2.15 33.8794 2.06 33.6794 1.88C33.4894 1.69 33.3944 1.455 33.3944 1.175C33.3944 0.895 33.4894 0.665 33.6794 0.484999C33.8794 0.295 34.1294 0.2 34.4294 0.2C34.7294 0.2 34.9744 0.295 35.1644 0.484999C35.3644 0.665 35.4644 0.895 35.4644 1.175C35.4644 1.455 35.3644 1.69 35.1644 1.88C34.9744 2.06 34.7294 2.15 34.4294 2.15ZM33.6344 11V3.56H35.2244V11H33.6344ZM40.4434 11C39.7134 11 39.1334 10.825 38.7034 10.475C38.2734 10.115 38.0584 9.48 38.0584 8.57V4.895H36.7834V3.56H38.0584L38.2534 1.67H39.6484V3.56H41.7484V4.895H39.6484V8.57C39.6484 8.98 39.7334 9.265 39.9034 9.425C40.0834 9.575 40.3884 9.65 40.8184 9.65H41.6734V11H40.4434ZM46.6774 11.18C45.9474 11.18 45.2974 11.02 44.7274 10.7C44.1674 10.37 43.7274 9.915 43.4074 9.335C43.0874 8.755 42.9274 8.08 42.9274 7.31C42.9274 6.53 43.0824 5.845 43.3924 5.255C43.7124 4.665 44.1524 4.205 44.7124 3.875C45.2824 3.545 45.9424 3.38 46.6924 3.38C47.4224 3.38 48.0574 3.545 48.5974 3.875C49.1374 4.195 49.5574 4.625 49.8574 5.165C50.1574 5.705 50.3074 6.3 50.3074 6.95C50.3074 7.05 50.3024 7.16 50.2924 7.28C50.2924 7.39 50.2874 7.515 50.2774 7.655H44.4874C44.5374 8.375 44.7724 8.925 45.1924 9.305C45.6224 9.675 46.1174 9.86 46.6774 9.86C47.1274 9.86 47.5024 9.76 47.8024 9.56C48.1124 9.35 48.3424 9.07 48.4924 8.72H50.0824C49.8824 9.42 49.4824 10.005 48.8824 10.475C48.2924 10.945 47.5574 11.18 46.6774 11.18ZM46.6774 4.685C46.1474 4.685 45.6774 4.845 45.2674 5.165C44.8574 5.475 44.6074 5.945 44.5174 6.575H48.7174C48.6874 5.995 48.4824 5.535 48.1024 5.195C47.7224 4.855 47.2474 4.685 46.6774 4.685ZM60.4764 11.18C59.4364 11.18 58.5364 10.955 57.7764 10.505C57.0264 10.045 56.4464 9.41 56.0364 8.6C55.6264 7.78 55.4214 6.835 55.4214 5.765C55.4214 4.685 55.6264 3.74 56.0364 2.93C56.4464 2.11 57.0264 1.47 57.7764 1.01C58.5364 0.55 59.4364 0.32 60.4764 0.32C61.7064 0.32 62.7114 0.62 63.4914 1.22C64.2714 1.82 64.7664 2.66 64.9764 3.74H63.2064C63.0664 3.13 62.7664 2.645 62.3064 2.285C61.8464 1.925 61.2314 1.745 60.4614 1.745C59.4114 1.745 58.5814 2.105 57.9714 2.825C57.3614 3.535 57.0564 4.515 57.0564 5.765C57.0564 7.005 57.3614 7.98 57.9714 8.69C58.5814 9.4 59.4114 9.755 60.4614 9.755C61.2314 9.755 61.8464 9.585 62.3064 9.245C62.7664 8.905 63.0664 8.44 63.2064 7.85H64.9764C64.7664 8.89 64.2714 9.705 63.4914 10.295C62.7114 10.885 61.7064 11.18 60.4764 11.18ZM70.1442 11.18C69.4342 11.18 68.7942 11.02 68.2242 10.7C67.6642 10.37 67.2192 9.915 66.8892 9.335C66.5592 8.745 66.3942 8.06 66.3942 7.28C66.3942 6.5 66.5592 5.82 66.8892 5.24C67.2292 4.65 67.6842 4.195 68.2542 3.875C68.8242 3.545 69.4592 3.38 70.1592 3.38C70.8692 3.38 71.5042 3.545 72.0642 3.875C72.6342 4.195 73.0842 4.65 73.4142 5.24C73.7542 5.82 73.9242 6.5 73.9242 7.28C73.9242 8.06 73.7542 8.745 73.4142 9.335C73.0842 9.915 72.6342 10.37 72.0642 10.7C71.4942 11.02 70.8542 11.18 70.1442 11.18ZM70.1442 9.815C70.5242 9.815 70.8742 9.72 71.1942 9.53C71.5242 9.34 71.7892 9.06 71.9892 8.69C72.1892 8.31 72.2892 7.84 72.2892 7.28C72.2892 6.72 72.1892 6.255 71.9892 5.885C71.7992 5.505 71.5392 5.22 71.2092 5.03C70.8892 4.84 70.5392 4.745 70.1592 4.745C69.7792 4.745 69.4242 4.84 69.0942 5.03C68.7742 5.22 68.5142 5.505 68.3142 5.885C68.1142 6.255 68.0142 6.72 68.0142 7.28C68.0142 7.84 68.1142 8.31 68.3142 8.69C68.5142 9.06 68.7742 9.34 69.0942 9.53C69.4142 9.72 69.7642 9.815 70.1442 9.815ZM75.6437 11V0.2H77.2337V11H75.6437ZM79.2472 11V0.2H80.8372V11H79.2472ZM86.3307 11.18C85.6007 11.18 84.9507 11.02 84.3807 10.7C83.8207 10.37 83.3807 9.915 83.0607 9.335C82.7407 8.755 82.5807 8.08 82.5807 7.31C82.5807 6.53 82.7357 5.845 83.0457 5.255C83.3657 4.665 83.8057 4.205 84.3657 3.875C84.9357 3.545 85.5957 3.38 86.3457 3.38C87.0757 3.38 87.7107 3.545 88.2507 3.875C88.7907 4.195 89.2107 4.625 89.5107 5.165C89.8107 5.705 89.9607 6.3 89.9607 6.95C89.9607 7.05 89.9557 7.16 89.9457 7.28C89.9457 7.39 89.9407 7.515 89.9307 7.655H84.1407C84.1907 8.375 84.4257 8.925 84.8457 9.305C85.2757 9.675 85.7707 9.86 86.3307 9.86C86.7807 9.86 87.1557 9.76 87.4557 9.56C87.7657 9.35 87.9957 9.07 88.1457 8.72H89.7357C89.5357 9.42 89.1357 10.005 88.5357 10.475C87.9457 10.945 87.2107 11.18 86.3307 11.18ZM86.3307 4.685C85.8007 4.685 85.3307 4.845 84.9207 5.165C84.5107 5.475 84.2607 5.945 84.1707 6.575H88.3707C88.3407 5.995 88.1357 5.535 87.7557 5.195C87.3757 4.855 86.9007 4.685 86.3307 4.685ZM94.1305 11.18C93.5005 11.18 92.9805 11.075 92.5705 10.865C92.1605 10.655 91.8555 10.38 91.6555 10.04C91.4555 9.69 91.3555 9.31 91.3555 8.9C91.3555 8.18 91.6355 7.61 92.1955 7.19C92.7555 6.77 93.5555 6.56 94.5955 6.56H96.5455V6.425C96.5455 5.845 96.3855 5.41 96.0655 5.12C95.7555 4.83 95.3505 4.685 94.8505 4.685C94.4105 4.685 94.0255 4.795 93.6955 5.015C93.3755 5.225 93.1805 5.54 93.1105 5.96H91.5205C91.5705 5.42 91.7505 4.96 92.0605 4.58C92.3805 4.19 92.7805 3.895 93.2605 3.695C93.7505 3.485 94.2855 3.38 94.8655 3.38C95.9055 3.38 96.7105 3.655 97.2805 4.205C97.8505 4.745 98.1355 5.485 98.1355 6.425V11H96.7555L96.6205 9.725C96.4105 10.135 96.1055 10.48 95.7055 10.76C95.3055 11.04 94.7805 11.18 94.1305 11.18ZM94.4455 9.89C94.8755 9.89 95.2355 9.79 95.5255 9.59C95.8255 9.38 96.0555 9.105 96.2155 8.765C96.3855 8.425 96.4905 8.05 96.5305 7.64H94.7605C94.1305 7.64 93.6805 7.75 93.4105 7.97C93.1505 8.19 93.0205 8.465 93.0205 8.795C93.0205 9.135 93.1455 9.405 93.3955 9.605C93.6555 9.795 94.0055 9.89 94.4455 9.89ZM103.034 8.72C102.644 8.72 102.284 8.675 101.954 8.585L101.309 9.215C101.419 9.285 101.554 9.345 101.714 9.395C101.874 9.445 102.099 9.49 102.389 9.53C102.679 9.57 103.074 9.61 103.574 9.65C104.564 9.74 105.279 9.98 105.719 10.37C106.159 10.75 106.379 11.26 106.379 11.9C106.379 12.34 106.259 12.755 106.019 13.145C105.789 13.545 105.424 13.865 104.924 14.105C104.434 14.355 103.804 14.48 103.034 14.48C101.994 14.48 101.149 14.28 100.499 13.88C99.8594 13.49 99.5394 12.895 99.5394 12.095C99.5394 11.785 99.6194 11.475 99.7794 11.165C99.9494 10.865 100.214 10.58 100.574 10.31C100.364 10.22 100.179 10.125 100.019 10.025C99.8694 9.915 99.7344 9.805 99.6144 9.695V9.335L100.904 8.015C100.324 7.515 100.034 6.86 100.034 6.05C100.034 5.56 100.149 5.115 100.379 4.715C100.619 4.305 100.964 3.98 101.414 3.74C101.864 3.5 102.404 3.38 103.034 3.38C103.454 3.38 103.844 3.44 104.204 3.56H106.979V4.61L105.659 4.685C105.899 5.095 106.019 5.55 106.019 6.05C106.019 6.55 105.899 7 105.659 7.4C105.429 7.8 105.089 8.12 104.639 8.36C104.199 8.6 103.664 8.72 103.034 8.72ZM103.034 7.475C103.494 7.475 103.864 7.355 104.144 7.115C104.434 6.865 104.579 6.515 104.579 6.065C104.579 5.605 104.434 5.255 104.144 5.015C103.864 4.775 103.494 4.655 103.034 4.655C102.564 4.655 102.184 4.775 101.894 5.015C101.614 5.255 101.474 5.605 101.474 6.065C101.474 6.515 101.614 6.865 101.894 7.115C102.184 7.355 102.564 7.475 103.034 7.475ZM101.039 11.945C101.039 12.375 101.229 12.695 101.609 12.905C101.989 13.125 102.464 13.235 103.034 13.235C103.584 13.235 104.029 13.115 104.369 12.875C104.709 12.645 104.879 12.335 104.879 11.945C104.879 11.655 104.764 11.405 104.534 11.195C104.304 10.995 103.869 10.87 103.229 10.82C102.749 10.79 102.324 10.745 101.954 10.685C101.604 10.875 101.364 11.08 101.234 11.3C101.104 11.52 101.039 11.735 101.039 11.945ZM111.143 11.18C110.243 11.18 109.533 10.9 109.013 10.34C108.503 9.78 108.248 8.945 108.248 7.835V3.56H109.838V7.67C109.838 9.11 110.428 9.83 111.608 9.83C112.198 9.83 112.683 9.62 113.063 9.2C113.443 8.78 113.633 8.18 113.633 7.4V3.56H115.223V11H113.813L113.693 9.695C113.463 10.155 113.123 10.52 112.673 10.79C112.233 11.05 111.723 11.18 111.143 11.18ZM120.725 11.18C119.995 11.18 119.345 11.02 118.775 10.7C118.215 10.37 117.775 9.915 117.455 9.335C117.135 8.755 116.975 8.08 116.975 7.31C116.975 6.53 117.13 5.845 117.44 5.255C117.76 4.665 118.2 4.205 118.76 3.875C119.33 3.545 119.99 3.38 120.74 3.38C121.47 3.38 122.105 3.545 122.645 3.875C123.185 4.195 123.605 4.625 123.905 5.165C124.205 5.705 124.355 6.3 124.355 6.95C124.355 7.05 124.35 7.16 124.34 7.28C124.34 7.39 124.335 7.515 124.325 7.655H118.535C118.585 8.375 118.82 8.925 119.24 9.305C119.67 9.675 120.165 9.86 120.725 9.86C121.175 9.86 121.55 9.76 121.85 9.56C122.16 9.35 122.39 9.07 122.54 8.72H124.13C123.93 9.42 123.53 10.005 122.93 10.475C122.34 10.945 121.605 11.18 120.725 11.18ZM120.725 4.685C120.195 4.685 119.725 4.845 119.315 5.165C118.905 5.475 118.655 5.945 118.565 6.575H122.765C122.735 5.995 122.53 5.535 122.15 5.195C121.77 4.855 121.295 4.685 120.725 4.685Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </Header>
        <div className="flex justify-between mt-4 h-full pr-4 lg:pr-8 sm:pr-6">
          {displayChatScreen && (
            <div className="shadow-lg absolute bottom-5 right-1 h-96 w-72 rounded-lg border-2 border-primary">
              <div className="flex justify-between bg-primary content-center sticky top-0 text-white p-2 py-3  ">
                <p className="text-white">
                  {peers.filter((peer) => peer.id === peerChatId)[0].name}
                </p>
                <button onClick={handleCloseChat}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className=" flex bg-white flex-col justify-end items-end gap-2 w-full h-3/4 py-3 px-2">
                {chats.map((chat, index) => {
                  return (
                    <p
                      className="break-all bg-chat-yellow p-2 rounded-lg text-left"
                      key={index}>
                      {chat.message}
                    </p>
                  );
                })}
              </div>

              <form
                className="bottom-0 h-[12%] rounded-b-lg right-0 w-full bg-neutral-200"
                onSubmit={onChatSend}>
                <div className=" flex	w-full items-center px-2">
                  <input
                    type="text"
                    className="w-full border-0 bg-neutral-200 focus:outline-none"
                    value={chatMessage}
                    placeholder="Type a message"
                    onChange={(e) => setChatMessage(e.target.value)}
                  />
                  <button className="">
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0.762767 6.58924C0.295267 6.40133 0.300767 6.12174 0.793933 5.95766L18.2894 0.125828C18.7743 -0.0355057 19.0521 0.235828 18.9164 0.710661L13.9169 18.2062C13.7794 18.6911 13.4815 18.7131 13.2597 18.2703L9.08335 9.91674L0.762767 6.58924ZM5.24527 6.40591L10.4116 8.47299L13.1983 14.0482L16.4488 2.67233L5.24435 6.40591H5.24527Z"
                        fill="#546CCC"
                      />
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          )}
          {/* Video tab */}
          <div className="bg-gradient-to-b from-[#383838] to-[#1F1F1F] w-3/5 rounded-lg relative flex flex-col justify-between max-h-[85vh]">
            <div className="flex w-full h-full items-center justify-center relative">
              {isCheckedIn && (
                <>
                  {peers
                    .filter((peer) => peer.roleName === "patient")
                    .map((peer) => (
                      <Peer peer={peer} key={peer.id} />
                    ))}
                  <div className="absolute right-0 bottom-0 rounded-lg ">
                    {peers
                      .filter((peer) => peer.isLocal)
                      .map((peer) => (
                        <Peer peer={peer} key={peer.id} />
                      ))}
                  </div>
                </>
              )}
            </div>

            {/* Video Options */}
            <div className="bg-[#383838] p-5 flex justify-between rounded-b-lg">
              <div>
                <div className="flex gap-3">
                  <button
                    className={
                      isLocalAudioEnabled
                        ? "px-4 py-4 bg-primary rounded"
                        : "px-4 py-4 bg-red-500 rounded"
                    }
                    onClick={toggleAudio}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M10.3838 13.1598C12.1357 13.1598 13.5547 11.7408 13.5547 9.98886V5.62889C13.5547 3.87698 12.1357 2.45801 10.3838 2.45801C8.63186 2.45801 7.21289 3.87698 7.21289 5.62889V9.98886C7.21289 11.7408 8.63186 13.1598 10.3838 13.1598Z"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4.32031 8.52148V9.86911C4.32031 13.2144 7.03935 15.9334 10.3846 15.9334C13.7299 15.9334 16.449 13.2144 16.449 9.86911V8.52148"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.3828 15.9338V18.312"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className={
                      isLocalVideoEnabled
                        ? "px-4 py-4 bg-primary rounded"
                        : "px-4 py-4 bg-red-500 rounded"
                    }
                    onClick={toggleVideo}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M10.1618 17.0594H5.15181C2.64681 17.0594 1.81445 15.3947 1.81445 13.722V7.04732C1.81445 4.54232 2.64681 3.70996 5.15181 3.70996H10.1618C12.6668 3.70996 13.4992 4.54232 13.4992 7.04732V13.722C13.4992 16.227 12.6589 17.0594 10.1618 17.0594Z"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M15.7018 14.4275L13.498 12.8817V7.87964L15.7018 6.33383C16.7799 5.58075 17.6678 6.04052 17.6678 7.36437V13.4049C17.6678 14.7288 16.7799 15.1885 15.7018 14.4275Z"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9.34533 9.59203C10.002 9.59203 10.5344 9.05966 10.5344 8.40295C10.5344 7.74624 10.002 7.21387 9.34533 7.21387C8.68862 7.21387 8.15625 7.74624 8.15625 8.40295C8.15625 9.05966 8.68862 9.59203 9.34533 9.59203Z"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <button
                    className="px-4 py-4 bg-zinc-600 rounded"
                    onClick={handleLinkCopy}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="white"
                      className="w-5 h-5">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                      />
                    </svg>
                  </button>
                  <button
                    className={
                      peerChatId === currentPatient?.id
                        ? "px-4 py-4 bg-primary rounded"
                        : "px-4 py-4 bg-zinc-600 rounded"
                    }
                    onClick={() => handleOpenChat()}
                    disabled={!isCheckedIn}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M7.48277 15.9338H7.08641C3.91552 15.9338 2.33008 15.1411 2.33008 11.1775V7.21385C2.33008 4.04296 3.91552 2.45752 7.08641 2.45752H13.4282C16.5991 2.45752 18.1845 4.04296 18.1845 7.21385V11.1775C18.1845 14.3483 16.5991 15.9338 13.4282 15.9338H13.0318C12.7861 15.9338 12.5483 16.0527 12.3976 16.2509L11.2086 17.8363C10.6854 18.5339 9.82923 18.5339 9.30603 17.8363L8.11695 16.2509C7.99011 16.0765 7.69681 15.9338 7.48277 15.9338Z"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.4233 9.59216H13.4304"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.2533 9.59216H10.2605"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.08146 9.59216H7.08858"
                        stroke="white"
                        strokeWidth="1.57317"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <button
                className=" bg-red-500  text-white px-6 rounded"
                onClick={handleNextPatient}>
                Next Patient Please
              </button>
            </div>
          </div>
          {/* Right info tab */}
          <div className="bg-white rounded-lg w-1/3">
            <div className="w-full flex items-center justify-between mt-4 gap-2 px-1 overflow-scroll scrollbar-hide border-b-2 border-[#F1F1F1]">
              <button
                className={`flex p-2 gap-2 items-center justify-center border-solid border-b-4 border-bottom border-transparent `}>
                <svg
                  width="16"
                  height="12"
                  viewBox="0 0 16 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.99978 0C11.5944 0 14.5851 2.58667 15.2124 6C14.5858 9.41333 11.5944 12 7.99978 12C4.40511 12 1.41444 9.41333 0.787109 6C1.41378 2.58667 4.40511 0 7.99978 0ZM7.99978 10.6667C9.35942 10.6664 10.6787 10.2045 11.7417 9.35678C12.8047 8.50901 13.5484 7.32552 13.8511 6C13.5473 4.67554 12.8031 3.49334 11.7402 2.64668C10.6773 1.80003 9.35865 1.33902 7.99978 1.33902C6.64091 1.33902 5.32224 1.80003 4.25936 2.64668C3.19648 3.49334 2.45229 4.67554 2.14844 6C2.45117 7.32552 3.19489 8.50901 4.25787 9.35678C5.32085 10.2045 6.64013 10.6664 7.99978 10.6667ZM7.99978 9C7.20413 9 6.44107 8.68393 5.87846 8.12132C5.31585 7.55871 4.99978 6.79565 4.99978 6C4.99978 5.20435 5.31585 4.44129 5.87846 3.87868C6.44107 3.31607 7.20413 3 7.99978 3C8.79543 3 9.55849 3.31607 10.1211 3.87868C10.6837 4.44129 10.9998 5.20435 10.9998 6C10.9998 6.79565 10.6837 7.55871 10.1211 8.12132C9.55849 8.68393 8.79543 9 7.99978 9ZM7.99978 7.66667C8.4418 7.66667 8.86573 7.49107 9.17829 7.17851C9.49085 6.86595 9.66644 6.44203 9.66644 6C9.66644 5.55797 9.49085 5.13405 9.17829 4.82149C8.86573 4.50893 8.4418 4.33333 7.99978 4.33333C7.55775 4.33333 7.13383 4.50893 6.82127 4.82149C6.5087 5.13405 6.33311 5.55797 6.33311 6C6.33311 6.44203 6.5087 6.86595 6.82127 7.17851C7.13383 7.49107 7.55775 7.66667 7.99978 7.66667Z"
                    fill="currentColor"
                  />
                </svg>
                <div className="">Overview</div>
              </button>
              <button
                className={`flex flex-1 p-2 gap-2 items-center justify-center border-solid border-b-4 border-bottom border-transparent `}>
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.77616 3.33333H14.5002C14.677 3.33333 14.8465 3.40357 14.9716 3.5286C15.0966 3.65362 15.1668 3.82319 15.1668 4V13.3333C15.1668 13.5101 15.0966 13.6797 14.9716 13.8047C14.8465 13.9298 14.677 14 14.5002 14H2.50016C2.32335 14 2.15378 13.9298 2.02876 13.8047C1.90373 13.6797 1.8335 13.5101 1.8335 13.3333V2.66667C1.8335 2.48986 1.90373 2.32029 2.02876 2.19526C2.15378 2.07024 2.32335 2 2.50016 2H7.44283L8.77616 3.33333ZM3.16683 3.33333V12.6667H13.8335V4.66667H8.22416L6.89083 3.33333H3.16683ZM5.8335 12C5.8335 11.2928 6.11445 10.6145 6.61454 10.1144C7.11464 9.61428 7.79292 9.33333 8.50016 9.33333C9.20741 9.33333 9.88568 9.61428 10.3858 10.1144C10.8859 10.6145 11.1668 11.2928 11.1668 12H5.8335ZM8.50016 8.66667C8.05814 8.66667 7.63421 8.49107 7.32165 8.17851C7.00909 7.86595 6.8335 7.44203 6.8335 7C6.8335 6.55797 7.00909 6.13405 7.32165 5.82149C7.63421 5.50893 8.05814 5.33333 8.50016 5.33333C8.94219 5.33333 9.36611 5.50893 9.67867 5.82149C9.99123 6.13405 10.1668 6.55797 10.1668 7C10.1668 7.44203 9.99123 7.86595 9.67867 8.17851C9.36611 8.49107 8.94219 8.66667 8.50016 8.66667Z"
                    fill="#B7B9C1"
                  />
                </svg>

                <div className="">PatientInfo</div>
              </button>
              <button
                className={`flex flex-1 p-2 gap-2 items-center justify-center border-solid border-b-4 border-bottom border-transparent `}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.3333 1.33398C13.7013 1.33398 14 1.63265 14 2.00065V4.50532L12.6667 5.83865V2.66732H3.33333V13.334H12.6667V11.4953L14 10.162V14.0007C14 14.3687 13.7013 14.6673 13.3333 14.6673H2.66667C2.29867 14.6673 2 14.3687 2 14.0007V2.00065C2 1.63265 2.29867 1.33398 2.66667 1.33398H13.3333ZM14.5187 5.87265L15.4613 6.81532L10.276 12.0007L9.332 11.9993L9.33333 11.058L14.5187 5.87265ZM8.66667 8.00065V9.33398H5.33333V8.00065H8.66667ZM10.6667 5.33398V6.66732H5.33333V5.33398H10.6667Z"
                    fill="#565759"
                  />
                </svg>

                <div className="">Transcript</div>
              </button>
              <button
                className={`flex flex-1 p-2 gap-2 items-center justify-center border-solid border-b-4 border-bottom border-transparent `}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6.55342 5.86L5.33342 6.304V8.66667H4.00009V5.36667H4.01009L7.52209 4.088C7.68475 4.026 7.86209 3.99467 8.04342 4.00067C8.40576 4.0097 8.75628 4.1314 9.04625 4.34886C9.33623 4.56631 9.55125 4.8687 9.66142 5.214C9.78542 5.60267 9.89875 5.86533 10.0014 6.002C10.3117 6.41575 10.7141 6.75149 11.1768 6.98258C11.6394 7.21368 12.1496 7.33377 12.6668 7.33333V8.66667C11.9783 8.66742 11.2982 8.51552 10.6755 8.22191C10.0528 7.9283 9.50288 7.50028 9.06542 6.96867L8.60075 9.606L10.0001 10.78V15.3333H8.66675V11.4013L7.15342 10.132L6.66875 12.8833L2.07275 12.0733L2.30475 10.76L5.58742 11.3387L6.55342 5.86ZM9.00009 3.66667C8.64647 3.66667 8.30733 3.52619 8.05728 3.27614C7.80723 3.02609 7.66675 2.68696 7.66675 2.33333C7.66675 1.97971 7.80723 1.64057 8.05728 1.39052C8.30733 1.14048 8.64647 1 9.00009 1C9.35371 1 9.69285 1.14048 9.9429 1.39052C10.1929 1.64057 10.3334 1.97971 10.3334 2.33333C10.3334 2.68696 10.1929 3.02609 9.9429 3.27614C9.69285 3.52619 9.35371 3.66667 9.00009 3.66667Z"
                    fill="#565759"
                  />
                </svg>

                <div className="">Activity</div>
              </button>
            </div>
            {waitingList && waitingList.length > 0 && (
              <div className="mt-3">
                <div className="flex flex-col p-4 border-b-grey-500 border-b-2">
                  <div className="text-sm text-gray-500">Name</div>
                  <div className="mt-1 font-regular">
                    {waitingList[0].firstName} {waitingList[0].lastName}
                  </div>
                </div>
                <div className="flex flex-col p-4 border-b-grey-500 border-b-2">
                  <div className="text-sm text-gray-500">Demographics</div>
                  <div className="mt-1 font-regular">
                    {moment().diff(
                      moment.utc(waitingList[0].birthDate),
                      "years"
                    )}
                    y • {waitingList[0].gender} • {waitingList[0].timeZone}
                  </div>
                </div>
                <div className="flex flex-col p-4 border-b-grey-500 border-b-2">
                  <div className="text-sm text-gray-500">Symptoms</div>
                  <div className="mt-1 font-regular"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingRoom;
